import type { WorkspaceServerService } from '@affine/core/modules/cloud';
import type { Permission } from '@affine/graphql';
import { getWorkspaceInfoQuery, leaveWorkspaceMutation } from '@affine/graphql';
import { Store } from '@toeverything/infra';
import { map } from 'rxjs';

import type { GlobalState } from '../../storage';
import type { WorkspaceLocalState } from '../../workspace';
import {
  type CachedWorkspacePermissionRole,
  isCachedWorkspacePermissionRoleFresh,
  resolveWorkspaceLocalAuthorityAccountId,
  WORKSPACE_PERMISSION_CACHE_VERSION,
} from '../../workspace/utils/permission-cache-identity';

type WorkspacePermissionSnapshot = {
  isOwner: boolean;
  isAdmin: boolean;
  isTeam: boolean;
  role?: Permission;
};

const LOCAL_PERMISSION_SCOPE = '__local__';

export class WorkspacePermissionStore extends Store {
  constructor(
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly workspaceLocalState: WorkspaceLocalState,
    private readonly globalState?: GlobalState
  ) {
    super();
  }

  async fetchWorkspaceInfo(workspaceId: string, signal?: AbortSignal) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    const info = await this.workspaceServerService.server.gql({
      query: getWorkspaceInfoQuery,
      variables: {
        workspaceId,
      },
      context: { signal },
    });

    return info;
  }

  /**
   * @param workspaceName for send email
   */
  async leaveWorkspace(workspaceId: string) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    await this.workspaceServerService.server.gql({
      query: leaveWorkspaceMutation,
      variables: {
        workspaceId,
      },
    });
  }

  watchWorkspacePermissionCache(workspaceId: string) {
    return this.workspaceLocalState
      .watch<CachedWorkspacePermissionRole<Permission>>('permission')
      .pipe(
        map(cache => this.normalizeWorkspacePermissionCache(cache, workspaceId))
      );
  }

  setWorkspacePermissionCache(
    workspaceId: string,
    permission: WorkspacePermissionSnapshot
  ) {
    const scope = this.currentPermissionScope(workspaceId);
    if (!scope || !permission.role) {
      this.workspaceLocalState.del('permission');
      return;
    }

    this.workspaceLocalState.set<CachedWorkspacePermissionRole<Permission>>(
      'permission',
      {
        ...permission,
        ...scope,
        cachedAt: Date.now(),
        role: permission.role,
        version: WORKSPACE_PERMISSION_CACHE_VERSION,
      }
    );
  }

  private normalizeWorkspacePermissionCache(
    cache: CachedWorkspacePermissionRole<Permission> | undefined,
    workspaceId: string
  ): WorkspacePermissionSnapshot | undefined {
    if (!cache) {
      return undefined;
    }

    const scope = this.currentPermissionScope(workspaceId);
    if (
      !scope ||
      !isCachedWorkspacePermissionRoleFresh(cache, {
        accountId: scope.accountId,
        serverBaseUrl: scope.serverBaseUrl,
        serverId: scope.serverId,
        workspaceId: scope.workspaceId,
      })
    ) {
      return undefined;
    }

    return {
      isAdmin: cache.isAdmin,
      isOwner: cache.isOwner,
      isTeam: cache.isTeam,
      role: cache.role,
    };
  }

  private currentPermissionScope(workspaceId: string): {
    accountId: string;
    serverBaseUrl: string;
    serverId: string;
    workspaceId: string;
  } | null {
    const server = this.workspaceServerService.server;
    if (!server) {
      return {
        accountId: LOCAL_PERMISSION_SCOPE,
        serverBaseUrl: LOCAL_PERMISSION_SCOPE,
        serverId: LOCAL_PERMISSION_SCOPE,
        workspaceId,
      };
    }
    const accountId = resolveWorkspaceLocalAuthorityAccountId({
      liveAccountId: server.confirmedAccountId,
      cachedAccountId: server.cachedAccountId,
      scopeReader: this.globalState,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
      workspaceId,
    });
    if (!accountId) {
      return null;
    }
    return {
      accountId,
      serverBaseUrl: server.baseUrl,
      serverId: server.id,
      workspaceId,
    };
  }
}
